import {
  ArrowPointerIcon,
  PanHandIcon,
  ResetIcon,
} from '@hakimo-ui/hakimo/ui-elements';
import {
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { ReactNode } from 'react';

interface Props {
  isZoomed: boolean;
  isPanning: boolean;
  showZoomControls: boolean;
  onPointer: () => void;
  onPan: () => void;
  onReset: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  trailing?: ReactNode;
}

const buttonClassName =
  'rounded p-1 text-white outline-none hover:bg-white/20 disabled:opacity-40 disabled:hover:bg-transparent';

// Flat controls sitting directly on the feed, unlike the shared ZoomControls
// which render inside a dark rounded bar.
export function TileZoomControls(props: Props) {
  const {
    isZoomed,
    isPanning,
    showZoomControls,
    onPointer,
    onPan,
    onReset,
    onZoomIn,
    onZoomOut,
    trailing,
  } = props;

  if (!showZoomControls && !trailing) {
    return null;
  }

  return (
    <div
      className={clsx(
        'absolute right-1 top-1 z-10 flex items-center gap-1.5 transition-opacity',
        !isZoomed && 'opacity-0 group-hover/tile:opacity-100'
      )}
    >
      {showZoomControls && (
        <div className="flex items-center gap-0.5 rounded-md bg-black/40 backdrop-blur-sm">
          <button
            type="button"
            onClick={onPointer}
            title="Pointer"
            className={clsx(buttonClassName, !isPanning && 'bg-white/20')}
          >
            <ArrowPointerIcon className="h-4 w-4 fill-white stroke-white" />
          </button>
          <button
            type="button"
            onClick={onPan}
            title="Pan"
            disabled={!isZoomed}
            className={clsx(buttonClassName, isPanning && 'bg-white/20')}
          >
            <PanHandIcon className="h-4 w-4 fill-white" />
          </button>
          <button
            type="button"
            onClick={onReset}
            title="Reset"
            disabled={!isZoomed}
            className={buttonClassName}
          >
            <ResetIcon className="h-4 w-4 fill-white" />
          </button>
          <button
            type="button"
            onClick={onZoomOut}
            title="Zoom Out"
            disabled={!isZoomed}
            className={buttonClassName}
          >
            <MagnifyingGlassMinusIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onZoomIn}
            title="Zoom In"
            className={buttonClassName}
          >
            <MagnifyingGlassPlusIcon className="h-4 w-4" />
          </button>
        </div>
      )}
      {trailing && (
        <div className="rounded-md bg-black/40 backdrop-blur-sm">
          {trailing}
        </div>
      )}
    </div>
  );
}

export default TileZoomControls;
